import React, { useEffect, useState, useRef } from "react";
import jwtDecode from "jwt-decode";

import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";
import CustomTable from "../../../components/table/table";
import ConfirmDialog from "../../../components/actionDialogs/confirmDialog";
import TextEditer from "../../../components/inputs/texteditor";

import {
  adminBulkEmailsList,
  superadminBulkEmailsList,
  adminBulkEmailDelete,
  superadminBulkEmailDelete,
  superadminBulkEmailDetail,
  superadminBulkEmailUpdate,
} from "../admin.api";

export default function BulkEmailsList(props) {
  const [data, setData] = useState([]);
  const [isActive, setIsActive] = useState(true);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const [openAction, setOpenAction] = useState(false);
  const [activeData, setActiveData] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editEmailUuid, setEditEmailUuid] = useState(null);
  const [editEditorKey, setEditEditorKey] = useState(0);
  const [fileInputKey, setFileInputKey] = useState(0);
  const [editSelectedFiles, setEditSelectedFiles] = useState([]);
  const [editExistingAttachments, setEditExistingAttachments] = useState([]);
  const [editFormData, setEditFormData] = useState({
    subject: "",
    body_html: "",
    body_text: "",
    metadata: {
      campaign: "",
      priority: "normal",
    },
  });

  let adminType = Number(jwtDecode(props.state.payload).user_type);

  const fileInputRef = useRef(null);

  const fetchBulkEmails = () => {
    setIsActive(true);

    // Fetch a large page of results so the listing behaves like Vendors list (no UI pagination)
    const defaultPage = 1;
    const defaultPerPage = 1000;

    const apiCall =
      adminType === 2
        ? superadminBulkEmailsList(defaultPage, defaultPerPage)
        : adminBulkEmailsList(defaultPage, defaultPerPage);

    apiCall
      .then((res) => {
        if (res?.success && res.data) {
          // Laravel-style paginator: res.data = { current_page, last_page, data: [...] }
          const pageData = res.data;
          const tdata = pageData.data || [];
          setData(tdata);
        } else {
          setData([]);
        }
        setIsActive(false);
      })
      .catch((error) => {
        console.error("Error fetching bulk emails:", error);
        setSnackMessage("Failed to load bulk emails");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsActive(false);
      });
  };

  useEffect(() => {
    fetchBulkEmails();
  }, []);

  const snackbarClose = (flag) => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
    if (!flag) {
      fetchBulkEmails();
    }
  };

  const handleView = (row) => {
    if (!row?.uuid) return;
    props.history.push(`/bulk-emails/${row.uuid}`);
  };

  const handleEdit = (row) => {
    if (adminType !== 2 || !row?.uuid) return;
    setIsEditModalOpen(true);
    setEditEmailUuid(row.uuid);
    setIsActive(true);

    superadminBulkEmailDetail(row.uuid)
      .then((res) => {
        const bulk = res?.data?.bulk_email;
        if (bulk) {
          let parsedMetadata = bulk.metadata || {};
          if (typeof parsedMetadata === "string") {
            try {
              parsedMetadata = JSON.parse(parsedMetadata);
            } catch (err) {
              parsedMetadata = {};
            }
          }
          setEditFormData({
            subject: bulk.subject || "",
            body_html: bulk.body_html || "",
            body_text: bulk.body_text || "",
            metadata: {
              campaign: parsedMetadata?.campaign || "",
              priority: parsedMetadata?.priority || "normal",
            },
          });
          setEditExistingAttachments(bulk.attachments || []);
          setEditEditorKey((prev) => prev + 1);
        }
      })
      .catch(() => {
        setSnackMessage("Failed to load bulk email for editing");
        setIsSnackError(true);
        setSnackOpen(true);
        setIsEditModalOpen(false);
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  const handleEditFormChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setEditFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value,
        },
      }));
    } else {
      setEditFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const handleEditModalClose = () => {
    setIsEditModalOpen(false);
    setEditEmailUuid(null);
    setEditEditorKey((prev) => prev + 1);
    setFileInputKey(prev => prev + 1);
    setEditSelectedFiles([]);
    setEditExistingAttachments([]);
    setEditFormData({
      subject: "",
      body_html: "",
      body_text: "",
      metadata: {
        campaign: "",
        priority: "normal",
      },
    });
  };

  const handleEditSubmit = () => {
    if (!editEmailUuid || adminType !== 2) return;
    setIsActive(true);

    const formData = new FormData();
    formData.append("subject", editFormData.subject);
    formData.append("body_html", editFormData.body_html);
    formData.append("body_text", editFormData.body_text);
    formData.append("metadata[campaign]", editFormData.metadata.campaign);
    formData.append("metadata[priority]", editFormData.metadata.priority);
    
    editSelectedFiles.forEach((file) => {
      formData.append("documents[]", file);
    });

    superadminBulkEmailUpdate(editEmailUuid, formData)
      .then((res) => {
        setSnackMessage(res?.message || "Bulk email updated successfully");
        setIsSnackError(false);
        setSnackOpen(true);
        handleEditModalClose();
        fetchBulkEmails();
      })
      .catch((error) => {
        setSnackMessage(error?.response?.data?.message || error?.message || "Failed to update bulk email");
        setIsSnackError(true);
        setSnackOpen(true);
        handleEditModalClose();
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  const handleEditFileChange = (e) => {
    const files = Array.from(e.target.files);
    const maxSize = 5 * 1024 * 1024; // 5 MB

    const oversized = files.filter(f => f.size > maxSize);
    if (oversized.length > 0) {
      setSnackMessage(`File size must be less than 5 MB`);
      setIsSnackError(true);
      setSnackOpen(true);
      e.target.value = ''; // clear the file input
      return;
    }

    setEditSelectedFiles(files);
  };

  const removeEditFile = (index) => {
    setEditSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setFileInputKey(prev => prev + 1); // force re-render to clear the file input
  };

  const handleDeleteClick = (row) => {
    setActiveData(row);
    setOpenAction(true);
  };

  const handleCancelDelete = () => {
    setOpenAction(false);
    setActiveData(null);
  };

  const handleConfirmDelete = () => {
    if (!activeData?.uuid) {
      handleCancelDelete();
      return;
    }
    setIsActive(true);

    const deleteCall =
      adminType === 2
        ? superadminBulkEmailDelete(activeData.uuid)
        : adminBulkEmailDelete(activeData.uuid);

    deleteCall
      .then((res) => {
        if (res?.success === false) {
          // API returned success: false with an error message
          setSnackMessage(res?.message || "Failed to delete bulk email");
          setIsSnackError(true);
        } else {
          setSnackMessage(res?.message || "Bulk email deleted successfully");
          setIsSnackError(false);
        }
        setSnackOpen(true);
      })
      .catch((error) => {
        const errorMsg = error?.response?.data?.message || error?.message || "Failed to delete bulk email";
        setSnackMessage(errorMsg);
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setIsActive(false);
        setOpenAction(false);
        setActiveData(null);
        fetchBulkEmails();
      });
  };

  return (
    <div>
      <Loader open={isActive} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />

      <div className="view-container">
        <div className="d-flex justify-content-between align-items-center">
          <h6 className="m-0">
            <b>Bulk Emails</b>
          </h6>
        </div>
        <hr />

        <CustomTable
          {...props}
          columns={[
            {
              title: "User Category",
              field: "user_category",
            },
            {
              title: "Subject",
              field: "subject",
            },
            {
              title: "Total Recipients",
              field: "total_recipients",
              render: (row) => row.total_recipients ?? "--",
            },
            {
              title: "Status",
              field: "status",
              render: (row) => {
                const raw = row.status || "";
                const value = String(raw).toLowerCase();

                // Normalize to three statuses: Sent, Submitted, Failed
                let label = raw;
                let className = "";

                if (value.includes("sent")) {
                  label = "Sent";
                  className = "Accepted"; // green-style badge
                } else if (value.includes("submit")) {
                  label = "Submitted";
                  className = "inprogress"; // blue/orange in-progress style
                } else if (
                  value.includes("fail") ||
                  value.includes("error") ||
                  value.includes("reject")
                ) {
                  label = "Failed";
                  className = "rejected"; // red-style badge
                }

                return <div className={className}>{label}</div>;
              },
            },
          ]}
          data={data}
          actions={[
            { type: "view", handleClick: handleView },
            ...(adminType === 2 ? [{ type: "edit", handleClick: handleEdit }] : []),
            ...(adminType === 2 ? [{ type: "delete", handleClick: handleDeleteClick }] : []),
          ]}
        />
      </div>
      {openAction && (
        <ConfirmDialog
          open={openAction}
          title="Delete Bulk Email?"
          data={activeData}
          message="Are you sure you want to delete this bulk email?"
          secondary_action="Cancel"
          primary_action="Delete"
          handleClose={handleCancelDelete}
          handleConfirm={handleConfirmDelete}
        />
      )}

      {isEditModalOpen && (
        <div
          className="modal-backdrop"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0,0,0,0.5)",
            zIndex: 1050,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: "1rem",
          }}
        >
          <div
            className="modal-content"
            style={{
              backgroundColor: "white",
              paddingLeft: "20px",
              borderRadius: "8px",
              width: "100%",
              maxWidth: "900px",
              maxHeight: "80vh",
              overflowY: "auto",
            }}
          >
            <div
              className="modal-header"
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h5 className="modal-title">Compose Email</h5>
              <button
                type="button"
                className="btn-close"
                onClick={handleEditModalClose}
                style={{
                  background: "none",
                  border: "none",
                  color: "white",
                  fontSize: "24px",
                  cursor: "pointer",
                  marginBottom: "10px",
                  fontWeight: "semibold",
                  opacity: 1,
                  filter: "none",
                }}
              >
                ×
              </button>
            </div>

            <div className="modal-body">
              <div className="mb-3">
                <label className="form-label">
                  Subject <span style={{ color: "red" }}>*</span>
                </label>
                <input
                  type="text"
                  className="form-control"
                  value={editFormData.subject}
                  onChange={(e) => handleEditFormChange("subject", e.target.value)}
                  placeholder="Enter email subject"
                />
              </div>

              <div className="mb-3">
                <TextEditer
                  label={
                    <>
                      Message <span style={{ color: "red" }}>*</span>
                    </>
                  }
                  key={`edit-html-${editEditorKey}`}
                  value={editFormData.body_html}
                  onChange={(value) => handleEditFormChange("body_html", value)}
                  required
                />
              </div>


              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Campaign</label>
                  <input
                    type="text"
                    className="form-control"
                    value={editFormData.metadata.campaign}
                    onChange={(e) => handleEditFormChange("metadata.campaign", e.target.value)}
                    placeholder="Campaign name"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Priority</label>
                  <select
                    className="form-select"
                    value={editFormData.metadata.priority}
                    onChange={(e) => handleEditFormChange("metadata.priority", e.target.value)}
                  >
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="low">Low</option>
                  </select>
                </div>
              </div>
                            <div className="mb-3">
                <label className="form-label">Attachments</label>
                {editExistingAttachments.length > 0 && (
                  <div className="mb-2">
                    <small className="text-muted">Existing attachments:</small>
                    {editExistingAttachments.map((att, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mt-1">
                        <small className="text-success">
                          {att.name} ({(att.size / 1024).toFixed(2)} KB)
                        </small>
                      </div>
                    ))}
                  </div>
                )}
                <input
                  key={fileInputKey}
                  type="file"
                  className="form-control"
                  multiple
                  onChange={handleEditFileChange}
                />
                {editSelectedFiles.length > 0 && (
                  <div className="mt-2">
                    <small className="text-muted">New files to add:</small>
                    {editSelectedFiles.map((file, index) => (
                      <div key={index} className="mt-1">
                        <small className="text-primary">
                          {file.name} ({(file.size / 1024).toFixed(2)} KB)
                        </small>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>

            <div
              className="modal-footer"
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
                marginTop: "20px",
              }}
            >
              <button type="button" className="btn btn-secondary" onClick={handleEditModalClose}
                style={{ minWidth: 120, height: 40 }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleEditSubmit}
                style={{ minWidth: 120, height: 40 }}
                disabled={!editFormData.subject || !editFormData.body_html}
              >
                Submit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
